import React from 'react';
import { X, Paperclip, CheckCircle2, Circle } from 'lucide-react';
import { PendingAttachment } from '@/types';
import { cn } from '@/lib/utils';

interface PendingAttachmentTileProps {
  attachment: PendingAttachment;
  isAndroid: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  canPreview: boolean;
  /** Result of the parent's `tileHandlers(key, onTap)` — spread onto the tile. */
  tileProps: React.HTMLAttributes<HTMLDivElement>;
  onRemove: () => void;
  /** Non-Android only: toggle this tile's selection from the hover/selected circle. */
  onToggleSelect?: () => void;
}

// Renders a single pending-attachment tile. All selection-key, long-press, and
// preview/remove wiring is computed by the parent and passed in via props so this
// component stays purely presentational.
export default function PendingAttachmentTile({
  attachment,
  isAndroid,
  selectionMode,
  isSelected,
  canPreview,
  tileProps,
  onRemove,
  onToggleSelect,
}: PendingAttachmentTileProps) {
  return (
    <div
      {...tileProps}
      className={cn(
        'relative aspect-square bg-secondary rounded-lg overflow-hidden group',
        (canPreview || (isAndroid && selectionMode)) && 'cursor-pointer',
        isAndroid && 'select-none',
        isSelected && 'ring-2 ring-primary'
      )}
    >
      {attachment.preview ? (
        <img
          src={attachment.preview}
          alt={attachment.file.name}
          className="w-full h-full object-cover pointer-events-none"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2 pointer-events-none">
          <Paperclip className="w-6 h-6 text-muted-foreground mb-1" />
          <span className="text-xs text-muted-foreground text-center truncate w-full">
            {attachment.file.name}
          </span>
        </div>
      )}
      {/* Selected overlay — both platforms (Android only selects in selectionMode). */}
      {isSelected && <div className="absolute inset-0 bg-primary/20 pointer-events-none" />}

      {isAndroid && selectionMode ? (
        /* Android: tap on the tile toggles selection, so the indicator is non-interactive. */
        <div className="absolute top-1 right-1 pointer-events-none">
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
          ) : (
            <Circle className="w-6 h-6 text-white/90 drop-shadow" />
          )}
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-1 left-1 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Non-Android: hover/selected select-circle (top-right). Only this toggles selection —
          the tile body keeps its preview/open behavior. */}
      {!isAndroid && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          className={cn(
            'absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity',
            isSelected && 'opacity-100'
          )}
          aria-label={isSelected ? 'בטל בחירה' : 'בחר'}
        >
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
          ) : (
            <Circle className="w-6 h-6 text-white/90 drop-shadow" />
          )}
        </button>
      )}
    </div>
  );
}
